<template>
  <div class="add-event">
    <h2 v-if="event.id" class="pt-2">{{$store.state.user.langEng ? 'Edit event' : 'Redigera händelse'}}</h2>
    <h2 v-else class="pt-2">{{$store.state.user.langEng ? 'New event' : 'Ny händelse'}}</h2>

    <form>
      <v-text-field
        class="event-title"
        :class="{'warning-outline': isCheckingUniqueTitle, 'error-outline': titleError }"
        prepend-icon="title"
        v-model="title"
        :label="$store.state.user.langEng ? 'Title' : 'Titel'"
      ></v-text-field>
      <calendar-date-time :error="dateError" />
      <v-text-field
        prepend-icon="location_on"
        v-model="location"
        :label="$store.state.user.langEng ? 'Location' : 'Plats'"
      ></v-text-field>
      <v-textarea
        prepend-icon="notes"
        name="input-7-1"
        label="Agenda"
        v-model="agenda"
        value="In markdown"
        auto-grow
        rows="1"
      ></v-textarea>
      <div class="row align-items-center mt-2 m-0">
        <v-icon class="mr-2" style="padding-bottom: 2px;" :color="color">fiber_manual_record</v-icon>
        <v-select
          class="event-color"
          :items="colors"
          item-text="text"
          item-value="color"
          v-model="color"
          solo
          flat
          background-color="transparent"
          dense
        >
          <template v-slot:item="{item}">
            <v-icon class="mr-2" :color="item.color">fiber_manual_record</v-icon>
            <p class="p-0 m-0">{{item.text}}</p>
          </template>
        </v-select>
      </div>
      <div class="col select-participants">
        <v-select
          :items="possibleUsers"
          v-model="users"
          solo
          item-avatar
          item-disabled="disabled"
          flat
          :label="$store.state.user.langEng ? 'Participants' : 'Deltagare'"
          multiple
          height="22px"
          background-color="transparent"
          dense
        >
          <template v-slot:item="{ item }">
            <div class="participant row justify-content-center">
              <v-avatar
                class="participant-avatar"
                :class="{'participant-avatar-selected' : users.find(p=>p.id == item.value.id)}"
                size="30px"
                color="gray"
              >
                <span class>{{item.value.name[0].toUpperCase()}}</span>
              </v-avatar>
              <span class="m-0">{{item.value.name}}</span>
            </div>
          </template>
        </v-select>
      </div>
      <div v-if="isAdmin" class="select-klass-course">
        <div class="bind-klass row align-items-center justify-content-between">
          <p
            class="text-start my-0"
          >{{$store.state.user.langEng ? 'Bind to class or course' : 'Bind till klass eller kurs'}}</p>
          <v-switch v-model="toggleCourse" class="col-1"></v-switch>
        </div>
        <select-course class="pt-0 m-0" :disable="!toggleCourse" :inEditEvent="true" />
      </div>
      <div class="row justify-content-around">
        <v-btn
          @click="cancelEditEvent"
          color="red lighten-2"
          dark
          type="button"
          :title="$store.state.user.langEng ? 'Press ESC to close' : 'Tryck ESC för att stänga'"
        >{{$store.state.user.langEng ? 'Cancel' : 'Avbryt'}}</v-btn>
        <v-btn
          @click.prevent="submitEvent"
          color="primary"
          type="button"
        >{{$store.state.user.langEng ? 'Save' : 'Spara'}}</v-btn>
      </div>
    </form>
  </div>
</template>

<script>
import { Vue, Component, Emit, Watch } from "vue-property-decorator";
import SelectCourse from "@/components/Calendar/SelectCourse";
import CalendarDateTime from "@/components/Calendar/CalendarDateTime";
import { Rest, Api } from "@/utils";

@Component({
  components: {
    SelectCourse,
    CalendarDateTime
  }
})
export default class AddEvent extends Vue {
  titleError = false;
  isCheckingUniqueTitle = false;
  toggleCourse = true;
  dateError = false;
  colors = [
    { text: "Salmon", color: "#EF9A9A" },
    { text: "Purple", color: "#AB47BC" },
    { text: "Red", color: "#E53935" },
    { text: "Deep Purple", color: "#5E35B1" },
    { text: "Blue", color: "#1E88E5" },
    { text: "Cyan", color: "#00ACC1" },
    { text: "Teal", color: "#26A69A" },
    { text: "Green", color: "#43A047" },
    { text: "Yellow", color: "#FDD835" },
    { text: "Charcoal", color: "#546E7A" }
  ];

  get event() {
    return this.$store.state.calendar.editEvent;
  }

  get title() { return this.$store.state.calendar.editEvent.title; }
  set title(title) { this.$store.commit("calendar/updateEditEvent", { title: title.trim() }); }

  get location() { return this.$store.state.calendar.editEvent.location; }
  set location(location) { this.$store.commit("calendar/updateEditEvent", { location: location.trim() }); }

  get agenda() { return this.$store.state.calendar.editEvent.agenda; }
  set agenda(agenda) { this.$store.commit("calendar/updateEditEvent", { agenda: agenda.trim() }); }

  get color() { return this.$store.state.calendar.editEvent.color; }
  set color(color) { this.$store.commit("calendar/updateEditEvent", { color }); }

  get users() { return this.$store.state.calendar.editEvent.users || []; }
  set users(users) { this.$store.commit("calendar/updateEditEvent", { users }); }

  @Watch("$store.state.calendar.editEvent.start")
  watchStartTime() {
    this.isCheckingUniqueTitle = true;
    this.checkUniqueTitle(this.title)
  }

  @Watch("$store.state.calendar.editEvent.title")
  watchTitle(title) {
    this.isCheckingUniqueTitle = true;
    clearTimeout(this.throttleTitle);

    this.throttleTitle = setTimeout(() => {
      this.checkUniqueTitle(title);
    }, 300)
  }

  @Watch('toggleCourse')
  watchToggleCourse(enabled) {
    if (!enabled) {
      this.$store.commit("calendar/updateEditEvent", {
        courses: [],
        klasses: [],
        schools: []
      });
    }
  }

  async checkUniqueTitle(title) {
    if (!title) {
      this.isCheckingUniqueTitle = false;
      this.titleError = false;
      return;
    }
    this.titleError = false;
    let results = await Api.get('getUniqueEvent', { title, timestamp: new Date(this.event.start).getTime() });
    this.isCheckingUniqueTitle = false;
    if (this.event.id && results.find(r => r.id == this.event.id)) { return; }
    this.titleError = !!results.length;
  }

  async submitEvent() {
    if (this.titleError === undefined) {
      this.isCheckingUniqueTitle = true;
      this.checkUniqueTitle(this.title);
      return;
    }
    if (this.titleError || this.isCheckingUniqueTitle) return;
    if (this.event.end < this.event.start) {
      this.dateError = true;
      return;
    }
    if (this.groomEvent()) return;

    let dbEvent = await this.sendToDatabase();
    if (dbEvent) {
      this.titleError = undefined;
      if (this.event.id) {
        this.$store.commit("calendar/removeEvent", this.event);
        this.event.id = '';
      }
      this.$store.commit("calendar/addNewEvent", dbEvent);
      if (this.event._id) {
        let cantUndoEvent = { ...dbEvent };
        delete cantUndoEvent._id;
        this.$store.commit("toggleSnackbar", {
          text: this.$store.state.user.langEng
            ? "Saved!"
            : "Sparad!",
          event: cantUndoEvent
        });
      } else {
        this.$store.commit("toggleSnackbar", {
          text: this.$store.state.user.langEng
            ? "Created a new event"
            : "Skapade en ny händelse",
          event: dbEvent
        });
      }
    } else {
      this.$store.commit("toggleSnackbar", {
        text: this.$store.state.user.langEng
          ? "Failed to create event"
          : "Ett fel uppstod vid skapandet av händelsen"
      });
    }
  }

  groomEvent() {
    if (!this.title) {
      this.titleError = true;
      return true;
    }
    if (!this.isAdmin) {
      this.watchToggleCourse(false);
    }
    return;
  }

  async sendToDatabase() {
    let event = { ...this.event };

    event.courses = this.$store.state.calendar.editEvent.courses;
    event.klasses = [];
    !event.courses.length && (event.klasses = this.$store.state.calendar.editEvent.klasses);
    event.schools = [];
    !event.klasses.length && !event.courses.length && (event.schools = this.$store.state.calendar.editEvent.schools);

    event.creator = this.user;

    // console.log("Sending CalendarEvent to backend:", event);

    let res;
    if (event._id) {
      res = await Rest.put("CalendarEvent", event).catch(console.error);
    } else {
      res = await Rest.post("CalendarEvent", event).catch(console.error);
    }

    return res;
  }

  @Emit("cancelEdit")
  cancelEditEvent() {
    return;
  }

  get possibleUsers() {
    let users = this.$store.state.users.map(obj => {
      return {
        text: obj.name,
        value: obj,
        disabled: obj.disabled
      };
    });

    return users
  }

  get user() {
    return this.$store.state.user.loggedInUser;
  }

  get isAdmin() {
    return this.$store.getters.isAdmin;
  }

  get isMobile() {
    return this.$store.state.isMobile;
  }
}
</script>

<style lang="scss">
.select-participants {
  padding: 0 0 0 2em !important;
  .v-text-field.v-text-field--enclosed > .v-input__control > .v-input__slot {
    padding: 0;
  }
}
.bind-klass {
  padding: 0 2.5em 0 0.45em;
}
.v-menu__content {
  border-radius: 4px !important;
  z-index: 204 !important;
  min-width: auto;
}
.v-calendar-weekly__day.v-present .v-calendar-weekly__day-label {
  font-weight: 700;
  border: none !important;
}
.add-event {
  .select-klass-course {
    padding-left: 2.3em;
  }
  .event-title .v-label {
    font-size: 22px;
  }
  .event-color {
    .v-input__slot {
      padding: 0 !important;
      margin: 0;
    }
    .v-text-field__details {
      margin: 0 !important;
    }
    input {
      display: none;
    }
  }
  .v-input--selection-controls {
    margin: 0;
    .v-input__slot {
      margin: 0 !important;
    }
  }
  .participant-avatar {
    background-color: #000;
  }
  .participant-avatar-selected {
    border: 1px solid var(--main-primary-color);
  }
}
</style>
